<script lang="ts" module>
  export interface SheetOption {
    value: string;
    label: string;
    /** Emoji shown before the label. */
    icon?: string;
    /** CSS colour of a dot shown before the label (priority). */
    dot?: string;
  }
</script>

<script lang="ts">
  /**
   * OptionSheet — a bottom-sheet picker (plans/mobile-v2.md §3.4) that a filter
   * chip opens: ≥48px rows with a check on the selected ones.
   *
   * - Single choice: tapping a row picks it and closes the sheet.
   * - `multiple`: rows toggle and the sheet stays open; the footer has Clear and Done.
   * - `searchable`: a 16px search field stays pinned above a long list.
   *
   * Stateless about the choice: the caller owns `selected` and applies `onChange`.
   */
  import MobileSheet from '../MobileSheet.svelte';
  import Icon from '../Icon.svelte';

  interface Props {
    open: boolean;
    onClose: () => void;
    title: string;
    options: SheetOption[];
    /** Selected option values. */
    selected: readonly string[];
    multiple?: boolean;
    searchable?: boolean;
    searchPlaceholder?: string;
    /** Shown when the search matches nothing. */
    emptyText?: string;
    onChange: (values: string[]) => void;
  }

  let {
    open,
    onClose,
    title,
    options,
    selected,
    multiple = false,
    searchable = false,
    searchPlaceholder = 'Search…',
    emptyText = 'No matches',
    onChange,
  }: Props = $props();

  let query = $state('');

  let visible = $derived.by(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  });

  function pick(value: string) {
    if (multiple) {
      onChange(
        selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
      );
    } else {
      onChange([value]);
      close();
    }
  }

  function close() {
    query = '';
    onClose();
  }
</script>

<MobileSheet {open} onClose={close} direction="bottom" {title}>
  <div class="options">
    {#if searchable}
      <div class="search">
        <input
          type="text"
          autocomplete="off"
          class="search-input"
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          bind:value={query}
        />
      </div>
    {/if}

    <div class="list" role={multiple ? 'group' : 'radiogroup'} aria-label={title}>
      {#each visible as option (option.value)}
        {@const on = selected.includes(option.value)}
        <button
          type="button"
          class="row"
          class:on
          role={multiple ? 'checkbox' : 'radio'}
          aria-checked={on}
          onclick={() => pick(option.value)}
        >
          {#if option.dot}<span class="dot" style="background: {option.dot};"></span>{/if}
          {#if option.icon}<span class="emoji" aria-hidden="true">{option.icon}</span>{/if}
          <span class="label">{option.label}</span>
          {#if on}<Icon name="check" size={18} strokeWidth={2} />{/if}
        </button>
      {:else}
        <p class="empty">{emptyText}</p>
      {/each}
    </div>

    {#if multiple}
      <div class="footer">
        <button
          type="button"
          class="btn ghost"
          disabled={selected.length === 0}
          onclick={() => onChange([])}>Clear</button
        >
        <button type="button" class="btn primary" onclick={close}>Done</button>
      </div>
    {/if}
  </div>
</MobileSheet>

<style>
  .options {
    display: flex;
    flex-direction: column;
  }

  .search {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 4px 16px 8px;
    background: #fff;
  }

  .search-input {
    width: 100%;
    height: 44px;
    padding: 0 12px;
    font: inherit;
    font-size: 16px;
    color: #111827;
    background: #f1f5f9;
    border: 0;
    border-radius: 10px;
    outline: none;
  }

  .search-input:focus-visible {
    box-shadow: 0 0 0 2px #93c5fd;
  }

  .list {
    display: flex;
    flex-direction: column;
    padding: 0 0 8px;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 48px;
    padding: 0 16px;
    font: inherit;
    font-size: 16px;
    color: #1f2937;
    text-align: left;
    background: none;
    border: 0;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  .row + .row {
    border-top: 1px solid #f3f4f6;
  }

  .row:active {
    background: #f3f4f6;
  }

  .row.on {
    color: #1d4ed8;
    font-weight: 600;
  }

  .row:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: -2px;
  }

  .label {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .dot {
    flex: none;
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }

  .emoji {
    flex: none;
  }

  .empty {
    margin: 0;
    padding: 24px 16px;
    color: #6b7280;
    text-align: center;
  }

  .footer {
    position: sticky;
    bottom: 0;
    display: flex;
    gap: 8px;
    padding: 8px 16px 12px;
    background: #fff;
    border-top: 1px solid #f3f4f6;
  }

  .btn {
    flex: 1;
    height: 48px;
    font: inherit;
    font-size: 16px;
    font-weight: 600;
    border: 0;
    border-radius: 12px;
    cursor: pointer;
  }

  .btn.ghost {
    color: #4b5563;
    background: #f3f4f6;
  }

  .btn.primary {
    color: #fff;
    background: #2563eb;
  }

  .btn:disabled {
    opacity: 0.5;
    cursor: default;
  }
</style>
